import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Slider from "@mui/material/Slider";
import Typography from "@mui/material/Typography";
import { Stack } from "@mui/material";
import { useSetAtom } from "jotai";
import { shouldCloseMenuAtom } from "atoms";
import CustomCheckIconButton from "./CustomCheckIconButton";

interface CustomSliderProps {
  header?: string;
  defaultValue: number;
  step: number;
  minLabel: string;
  maxLabel: string;
  labels: string[];
  disabled?: boolean;
  onSliderChange: (value: number) => void;
  onSubmit?: () => void;
  showCheckButton?: boolean;
  isVolumeMenu?: boolean;
  value?: number;
  showAllLabels?: boolean;
  id?: string;
}

export default function CustomSlider({
  value,
  defaultValue,
  step,
  minLabel,
  maxLabel,
  labels,
  disabled,
  header,
  onSliderChange,
  onSubmit,
  isVolumeMenu = false,
  showCheckButton = true,
  showAllLabels = false,
  id,
}: CustomSliderProps): JSX.Element {
  const range = labels.length - 1;
  const controlledValue = value !== undefined ? value : defaultValue;
  const [inputValue, setInputValue] = useState<string>(labels[controlledValue]);
  const setShouldCloseMenu = useSetAtom(shouldCloseMenuAtom);

  useEffect(() => {
    setInputValue(labels[controlledValue]);
  }, [controlledValue, labels]);

  // Generate marks for each step with or without labels
  const marks = showAllLabels
    ? Array.from({ length: range / step + 1 }).map((_, index) => ({
        value: index * step,
        label: labels[index * step],
      }))
    : Array.from({ length: range / step + 1 }).map((_, index) => ({
        value: index * step,
      }));

  const handleSliderChange = (_event: Event, newValue: number | number[]) => {
    const currentValue = Array.isArray(newValue) ? newValue[0] : newValue;
    onSliderChange(currentValue);
    setInputValue(labels[currentValue]);
  };

  useEffect(() => {
    setShouldCloseMenu(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = () => {
    setInputValue("");
    onSubmit?.();
  };

  return (
    <Stack direction="row" alignItems="center" mr={isVolumeMenu ? 0 : 2}>
      <Stack width="100%">
        <Box sx={{ margin: isVolumeMenu ? 0 : "0 16px" }}>
          {header && (
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{
                padding: "16px 0",
              }}
            >
              {header}
            </Typography>
          )}
          <Box sx={{ margin: isVolumeMenu ? 0 : "0 16px" }}>
            <Slider
              id={id || "custom-slider"}
              value={controlledValue}
              getAriaValueText={(value) => `${value}`}
              valueLabelDisplay={showAllLabels ? "off" : "auto"}
              valueLabelFormat={(value) =>
                labels[Math.round(value / (range / (labels.length - 1)))]
              }
              step={step}
              marks={marks}
              min={0}
              max={range}
              disabled={disabled}
              onChange={handleSliderChange}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            {!showAllLabels && (
              <>
                <Typography fontSize={13}>{minLabel}</Typography>
                <Typography fontSize={13}>{maxLabel}</Typography>
              </>
            )}
          </Box>
        </Box>
      </Stack>
      {showCheckButton && (
        <Stack mt={2}>
          <CustomCheckIconButton
            handleSubmit={handleSubmit}
            currentInputValue={inputValue}
          />
        </Stack>
      )}
    </Stack>
  );
}
